<template>
  <div class="filter-configurator">
    <template v-for="(hydratedFilter, index) in hydratedFilters" :key="hydratedFilter.id + 'f'">
      <div class="filter-configurator__item">
        <FilterRow
          :filter="hydratedFilter"
          v-bind="hydratedFilter.props"
          @update="(value) => updateFilter(index, value)"
          @remove="removeFilter(index)"
        >
          <template #filterSelector>
            <FilterSelector
              :filter-name="hydratedFilter.name"
              :filter-options="filterOptions"
              @select-filter-option="selectFilterOption(index, $event)"
            />
          </template>

          <template #logic>
            <LogicSelector :logic="modelValue.mode" :selection-enabled="index === 0" @select-logic="selectMode" />
          </template>
        </FilterRow>
      </div>
    </template>

    <div class="filter-configurator__item">
      <FilterAdd :modelValue="modelValue" :filters="filterOptions" @add="addFilter">
        <template #cta>
          <template v-if="!modelValue.filters.length">
            {{ $t('filter.addFirstRule', { entityType: $t(`entity.${entityType}`, 2) }) }}
          </template>
        </template>
      </FilterAdd>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { append, insert, map, prop, remove, update } from 'ramda';
// import type { Prop } from 'vue';
import { FilterOptions, Filter, HydratedFilter, FilterListLogic, Mode } from '@/filter/types';
import { createFilter } from '@/filter/createFilter';

import FilterAdd from './FilterAdd.vue';
import FilterRow from './FilterRow.vue';
import FilterSelector from './FilterSelector.vue';
import LogicSelector from './LogicSelector.vue';

type FilterConfiguratorValue = {
  filters: Array<Filter>;
  mode: FilterListLogic;
};

export default defineComponent({
  name: 'FilterConfigurator',

  props: {
    filterOptions: {
      type: Object as PropType<FilterOptions>,
      required: true,
    },

    entityType: {
      type: String,
      required: true,
    },

    modelValue: {
      type: Object as PropType<FilterConfiguratorValue>,
      required: true,
    },
  },

  components: {
    FilterAdd,
    FilterRow,
    FilterSelector,
    LogicSelector,
  },
  emits: { 'update:modelValue': null },

  computed: {
    hydratedFilters(): Array<HydratedFilter> {
      return map(
        (filter) => ({
          ...this.filterOptions[filter.name],
          ...filter,
        }),
        this.modelValue.filters
      );
    },
  },

  methods: {
    // TODO: simplify this method

    addFilter(filter: FilterOptions[0], name: string) {
      setTimeout(() => {
        const updatedFilter = {
          ...this.modelValue,
          filters: append(this.filter(name, filter), this.modelValue.filters),
        };

        this.$emit('update:modelValue', updatedFilter);
      }, 100);
    },

    updateFilter(index: number, value: { mode: Mode; value?: string | Array<string> }) {
      const updatedFilter = {
        ...this.modelValue.filters[index],
        value,
      };

      this.$emit('update:modelValue', {
        ...this.modelValue,
        filters: update(index, updatedFilter, this.modelValue.filters),
      });
    },

    removeFilter(index: number) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        filters: remove(index, 1, this.modelValue.filters),
      });
    },

    filter(name: string, filterOption: FilterOptions[0]): Filter {
      return createFilter(name, filterOption.type, filterOption.allowedModes);
    },

    selectFilterOption(index: number, selectedFilterOption) {
      const filterOption = prop(selectedFilterOption, this.filterOptions);

      const newFilter = this.filter(selectedFilterOption, filterOption);

      const filters = insert(index, newFilter, remove(index, 1, this.modelValue.filters));

      const updatedFilter = {
        ...this.modelValue,
        filters,
      };

      this.$emit('update:modelValue', updatedFilter);
    },

    selectMode(mode: Mode) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        mode,
      });
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
.filter-configurator {
  background-color: colors.$color-white;
  margin: -8px 0 0 -8px;
}

.filter-configurator__item {
  padding: 8px 0 0 8px;
  display: flex;

  .code-pill__name,
  .group-pill__select {
    font-size: 13px;
  }
}
</style>
